import {LFO, ENVELOPE} from 'constants/mod-types.js';
import {getNonOverlapping} from 'components/effects/rack/overlaps.js';
import {saveParameterToJson, loadParameterFromJson}  from 'parameters/parameterJsonFunctions.js';
import ParameterContext from 'effects/ParameterContext.js';
import EffectEdge from './EffectEdge.js';
const INPUT_ID = -1;

export default superclass => class extends superclass  {
    effects = [];
    adjacencyLists = {};
    inputNode = {
        id: -1,
        x: 0,
        y: 200
    };

    outputNode = {
        id: -2,
        x: 600,
        y: 200,
    };

    get nodes() {
        return this.effects;
    }

    getSubDevices() {
        return this.effects.map(
            effect => ({value: effect, label: effect.name}));
    }

    getSubDeviceIndex(subDevice) {
        return this.effects.indexOf(subDevice);
    }

    getSubDevice(index) {
        return this.effects[index];
    }

    getParameters() {
        if (!this.parameters) {
            return [];
        }
        let parameters = [... this.parameters];
        for (let i in this.effects) {
            parameters = [
                ... parameters,
                ...this.effects[i].getParameters()
            ];
        }
        return parameters;
    }

    get lfoAdjacencyLists() {
        return this.getModAdjacencyLists(LFO);
    }

    get envelopeAdjacencyLists() {
        return this.getModAdjacencyLists(ENVELOPE);
    }

    getModAdjacencyLists(type) {
        let modulators = [
            ... this.sequencerEngine.lfos.map(l => l.lfo),
            ... this.sequencerEngine.envelopes];
        let mods = modulators.filter(m => m.getType() === type);
        let adjLists = {};
        for (let i in mods) {
            let mod = mods[i];
            let parameters = this.getParameters()
                .filter(p => p.lfos && p.envelopes)
                .filter(
                    p => type === LFO ?
                        p.lfos.indexOf(mod) > -1 :
                        p.envelopes.indexOf(mod) > -1);
            if (parameters.length === 0) {
                continue;
            }

            adjLists[mod.number] = parameters;
        }
        return adjLists;
 
    }

    registerContext(context) {
        super.registerContext(context);
        this.effects.forEach(e => e.registerContext(context, this));
    }

    get modulators() {
        return [
            ... this.sequencerEngine.lfos.map(l => l.lfo),
            ... this.sequencerEngine.envelopes];
    }

    setSize(width, height, nodeWidth, nodeHeight) {
        this.inputNode.x = 0;
        this.inputNode.y = height / 2 - nodeHeight / 2;
        this.outputNode.x = width - nodeWidth;
        this.outputNode.y = height / 2 - nodeHeight / 2;
    }

    addEffect(effect, setWet=true) {
        if (!effect.x) {
            effect.x = 200;
            effect.y = 200;
        }
        let context = (this.context || new ParameterContext()).getCopy();
        context.forEffect(this);
        effect.registerContext(context, this);
        this.effects = [... this.effects, effect];
        effect.rack = this;
    }

    deleteEffect(effect, dispose=true) {
        let edges = this.adjacencyLists[effect.id] || [];
        [...edges].forEach(e => this.deleteEdge(e));
        delete this.adjacencyLists[effect.id];
        for (let i in this.adjacencyLists) {
            edges = this.adjacencyLists[i].filter(x => x.effect === effect);
            edges.forEach(e => this.deleteEdge(e));
        }
        for (let i in this.lfoAdjacencyLists) {
            if (this.lfoAdjacencyLists[i] === undefined) {
                continue;
            }
            edges = this.lfoAdjacencyLists[i]
                .filter(param => effect.parameters.indexOf(param) > -1);
            edges.forEach(e => this.deleteEdge(e));
        }
        for (let i in this.envelopeAdjacencyLists) {
            if (this.envelopeAdjacencyLists[i] === undefined) {
                continue;
            }
            edges = this.envelopeAdjacencyLists[i]
                .filter(param => effect.parameters.indexOf(param) > -1);
            edges.forEach(e => this.deleteEdge(e));
        }
        if (dispose) {
            effect.dispose();
        }
        this.effects.splice(this.effects.indexOf(effect), 1);
    }

    deleteEdge(edge, dispose=true) {
        if (edge.macro) {
            let parameter = edge.parameter;
            let macro = edge.macro;
            macro.unmapParameter(parameter, false);
        } else if (edge.modulator) {
            let parameter = edge.edge;
            let modulator = edge.modulator;
            if (modulator.getType() === LFO) {
                parameter.disconnectFromLFO(modulator);
            } else {
                parameter.disconnectFromEnvelope(modulator);
            }
        } else {
            for (let id in this.adjacencyLists) {
                let adjacencyList = this.adjacencyLists[id];
                let indexOf =  adjacencyList.indexOf(edge); 
                if (indexOf > -1) {
                    adjacencyList.splice(indexOf, 1);
                }
            }
        }
        if (edge.dispose && dispose) {
            edge.dispose();
        }
    }

    getLFO(number) {
        return this.modulators
            .filter(m => m.getType() === LFO)
            .find(m => m.number === number);
    }

    getEnvelope(number) {
        return this.modulators
            .filter(m => m.getType() === ENVELOPE)
            .find(m => m.number === number);
    }

    connectEdge(node1, edge) {
        let effectId = undefined;
        edge.fromNode = node1;
        if (node1.effectNode || node1.track) {
            effectId = node1.id;
        } else {
            effectId = INPUT_ID;
        }

        if (this.adjacencyLists[effectId] === undefined) {
            this.adjacencyLists[effectId] = [];
        }
 
        this.adjacencyLists[effectId].push(edge);
        return edge;
    }

    // if node1 is undefined, then its INPUT
    // if node2 is undefined, then its OUTPUT
    // otherwise they are effect nodes in the effect graph
    connectNodes(node1=this.inputNode, node2=this.outputNode, outputNumber=0) {
        if (this.isMacro(node1)) {
            this.connectNodeToMacro(node2, node1);
        } else if (node1.getType) {
            // it's a modulator
            if (node2.isAudioParamEnabled && !node2.isAudioParamEnabled()) {
                node2.enableAudioParam();
            }
            if (node1.getType() === LFO && node2.connectToLFO) {
                node2.connectToLFO(node1);
            } else if (node2.connectToEnvelope) {
                node2.connectToEnvelope(node1);
            }
        } else if (!node2.isAudioParamEnabled) {
            // check if theres already a connection

            if (this.adjacencyLists[node1.id] && this.adjacencyLists[node1.id].some(
                x => x.effect === node2)) {
                
                return undefined;
            }
            // it's 2 effects
            let edge = this.newEdge(node2, outputNumber); 
            this.connectEdge(node1, edge);
            return edge;
        }
    }

    newEdge(node2, outputNumber) {
        return new EffectEdge(node2, this.sequencerEngine, outputNumber);
    }

    getRackJson() {
        let adjacencyLists = {};
        for (let id in this.adjacencyLists) {
            let adjList = this.adjacencyLists[id];
            let retList = adjList.map(
                edge => edge.getJson(this));
            if (parseInt(id) === -1) {
                adjacencyLists[id] = retList;
            } else {
                let effect = this.effects.find(effect => effect.id === id);
                adjacencyLists[this.effects.indexOf(effect)] = retList;
            }
        }

        this.saveCurrentConfiguration();

        let json = {
            ... super.getJson(),
            ... json,
            configurations: this.getConfigurationJson(),
            outputPosition: {
                x: this.outputNode.x,
                y: this.outputNode.y,
            },
            currentConfiguration: this.currentConfiguration,
            macros: this.macros.map(
                m => m.getJson()),
            adjacencyLists: adjacencyLists,
            effects: this.effects.map(e => ({
                ... e.getJson(),
                _x: e.x,
                _y: e.y
            }))
        };
        json = saveParameterToJson(json, 'configurationParameter', this.configurationParameter);
        return json;
    }

    fromRackJson(json, isCopy) {
        this.loaded = false;

        if (json.outputPosition) {
            this.outputNode.x = json.outputPosition.x;
            this.outputNode.y = json.outputPosition.y;
        }

        if (json.configurations) {
            this.loadConfigurationJson(json.configurations);
        }

        let {adjacencyLists} = json;
        for (let id in adjacencyLists) {
            let effect = this.effects[id];
            if (!effect) {
                effect = this.inputNode;
            }
            for (let i in adjacencyLists[id]) {
                let edge = this.newEdge(undefined);
                edge.fromJson(adjacencyLists[id][i], this);
                this.connectEdge(effect, edge);
            }
        }

        if (json.currentConfiguration !== undefined) {
            this.currentConfiguration = json.currentConfiguration;
        }

        this.loadParameterFromJson(
            json,
            'configurationParameter',
            this.configurationParameter,
            isCopy);

        if (json.macros) {
            let parameters = this.getParameters();
            for (let i in json.macros) {
                let context = this.context || new ParameterContext();
                let macro = this.newMacro(context.getCopy().forEffect(this));
                macro.fromJson(json.macros[i], parameters);
            }
        }

        this.loaded = true;
    }

    pasteNodes(nodes) {
        let copied = nodes.map(x => x.getEffectCopy());
        for (let copy of copied) {
            this.addEffect(copy);
            let {x, y} = getNonOverlapping(nodes[0].x, nodes[0].y, this);
            copy.x = x;
            copy.y = y;
        }

        for (let i in nodes) {
            let listOfConnections = this.adjacencyLists[nodes[i].id];
            if (!listOfConnections) {
                continue;
            }
            let fromNode = copied[i];
            for (let edge of listOfConnections) {
                // need to find the copy
                let index = nodes.indexOf(edge.effect);
                if (index > -1) {
                    this.connectNodes(fromNode, copied[index]);
                } else {
                    this.connectNodes(fromNode, edge.effect);
                }
            }
        }
        // need to copy the configuration and place it in a good place
    }

    connectNodesInSeries(nodes) {
        nodes.sort((a,b) => a.x - b.x);
        let previous = undefined;
        for (let node of nodes) {
            if (previous) {
                this.connectNodes(previous, node);
            }
            previous = node;
        }
    }

    clearRack() {
        this.effects.forEach(
            e => this.deleteEffect(e, false));
    }
}

